import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import api from '../services/api';
import { useFamily } from '../contexts/FamilyContext';

const Events = () => {
  const navigate = useNavigate();
  const { currentFamily } = useFamily();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    host: '',
    event_date: '',
    location: '',
    event_type: 'holiday',
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const fetchEvents = useCallback(async () => {
    if (!currentFamily) {
      setLoading(false);
      return;
    }
    try {
      const response = await api.get(`/events/?family_id=${currentFamily.id}`);
      const eventsList = response.data.results || response.data;
      setEvents(Array.isArray(eventsList) ? eventsList : []);
      setLoading(false);
    } catch (error) {
      setEvents([]);
      setLoading(false);
    }
  }, [currentFamily]);

  useEffect(() => {
    if (currentFamily) {
      fetchEvents();
    } else {
      setLoading(false);
      setEvents([]);
    }
  }, [currentFamily, fetchEvents]);

  const handleCreate = async () => {
    if (!currentFamily) {
      alert('Please select or create a family first');
      return;
    }
    
    // Validate that currentFamily has a valid ID
    if (!currentFamily.id) {
      alert('Invalid family selected. Please refresh the page or select a different family.');
      return;
    }
    
    // Validate required fields
    if (!formData.title || !formData.title.trim()) {
      alert('Please enter an event title');
      return;
    }
    if (!formData.host || !formData.host.trim()) {
      alert('Please enter an event host');
      return;
    }
    if (!formData.event_date) {
      alert('Please select an event date and time');
      return;
    }
    if (!formData.location || !formData.location.trim()) {
      alert('Please enter an event location');
      return;
    }
    
    // Convert datetime-local to ISO string with timezone
    // datetime-local gives us "YYYY-MM-DDTHH:mm" in local time
    // We need to convert it to ISO format that Django can parse correctly
    const localDate = new Date(formData.event_date);
    const eventDateISO = localDate.toISOString();
    
    try {
      // Use FormData if there's an image file, otherwise use regular JSON
      let response;
      if (imageFile) {
        const formDataToSend = new FormData();
        formDataToSend.append('title', formData.title.trim());
        formDataToSend.append('description', formData.description || '');
        formDataToSend.append('host', formData.host.trim());
        // Convert datetime-local to ISO format for Django
        formDataToSend.append('event_date', eventDateISO);
        formDataToSend.append('location', formData.location.trim());
        formDataToSend.append('event_type', formData.event_type);
        formDataToSend.append('family_id', currentFamily.id.toString());
        formDataToSend.append('image', imageFile);
        
        response = await api.post('/events/', formDataToSend);
      } else {
        response = await api.post('/events/', {
          title: formData.title.trim(),
          description: formData.description || '',
          host: formData.host.trim(),
          event_date: formData.event_date,
          location: formData.location.trim(),
          event_type: formData.event_type,
          family_id: currentFamily.id,
        });
      }
      
      // Refresh events list to show the new event
      await fetchEvents();
      setOpen(false);
      setFormData({
        title: '',
        description: '',
        host: '',
        event_date: '',
        location: '',
        event_type: 'holiday',
      });
      setImageFile(null);
      setImagePreview(null);
      
      // Show success message and optionally navigate
      alert('Event created successfully!');
      
      // Optionally navigate to event detail page
      if (response.data && response.data.id) {
        const goToDetail = window.confirm('Event created! Would you like to view the event details?');
        if (goToDetail) {
      navigate(`/events/${response.data.id}`);
        }
      }
    } catch (error) {
      
      // If the error is about family not found, refresh families
      if (error.response?.data?.error && 
          (error.response.data.error.includes('not a member of family') || 
           error.response.data.error.includes('Family not found'))) {
        alert('The selected family is no longer available. Refreshing family list...');
        // Trigger a refresh of families
        window.location.reload();
      } else {
        // Show detailed validation errors if available
        let errorMessage = 'Failed to create event';
        if (error.response?.data?.details) {
          // Format validation errors
          const details = error.response.data.details;
          const errorList = Object.entries(details)
            .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
            .join('\n');
          errorMessage = `Validation errors:\n${errorList}`;
        } else if (error.response?.data?.error) {
          errorMessage = error.response.data.error;
        } else if (error.response?.data?.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.message) {
          errorMessage = error.message;
        }
        alert(errorMessage);
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Celebrations</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpen(true)}
        >
          Create Event
        </Button>
      </Box>

      {!currentFamily ? (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              No Family Selected
            </Typography>
            <Typography>
              Please create or join a family to start creating events.
            </Typography>
            <Button
              variant="contained"
              sx={{ mt: 2 }}
              onClick={() => navigate('/families')}
            >
              Go to Families
            </Button>
          </CardContent>
        </Card>
      ) : loading ? (
        <Typography>Loading...</Typography>
      ) : events.length === 0 ? (
        <Card>
          <CardContent>
            <Typography>No events yet. Create your first event!</Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {events.map((event) => (
            <Grid item xs={12} sm={6} md={6} key={event.id}>
              <Card
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/events/${event.id}`)}
              >
                {event.image_url_display && (
                  <CardMedia
                    component="img"
                    height={200}
                    image={event.image_url_display}
                    alt={event.title}
                      onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                    onLoad={() => {
                      // Image loaded successfully
                    }}
                    sx={{ 
                      objectFit: 'cover',
                      bgcolor: 'rgba(0,0,0,0.05)',
                    }}
                  />
                )}
                <CardContent>
                  <Typography variant="h5" gutterBottom>
                    {event.title}
                  </Typography>
                  <Box sx={{ mb: 1 }}>
                    {event.event_type && (
                      <Chip
                        label={event.event_type.charAt(0).toUpperCase() + event.event_type.slice(1)}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Host: {event.host}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    📅 {formatDate(event.event_date)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    📍 {event.location}
                  </Typography>
                  {event.description && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {event.description}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog 
        open={open} 
        onClose={() => setOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { m: { xs: 1, sm: 2 }, maxHeight: '90vh' }
        }}
      >
        <DialogTitle>Create New Event</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label="Event Title"
            margin="normal"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          />
          <TextField
            fullWidth
            label="Host Name"
            margin="normal"
            value={formData.host}
            onChange={(e) => setFormData({ ...formData, host: e.target.value })}
          />
          <TextField
            fullWidth
            label="Location"
            margin="normal"
            value={formData.location}
            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
          />
          <TextField
            fullWidth
            label="Event Date & Time"
            type="datetime-local"
            margin="normal"
            value={formData.event_date}
            onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Event Type</InputLabel>
            <Select
              value={formData.event_type}
              onChange={(e) => setFormData({ ...formData, event_type: e.target.value })}
            >
              <MenuItem value="holiday">Holiday</MenuItem>
              <MenuItem value="birthday">Birthday</MenuItem>
              <MenuItem value="anniversary">Anniversary</MenuItem>
              <MenuItem value="wedding">Wedding</MenuItem>
              <MenuItem value="graduation">Graduation</MenuItem>
              <MenuItem value="party">Party</MenuItem>
              <MenuItem value="gathering">Gathering</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Description"
            margin="normal"
            multiline
            rows={3}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
          <TextField
            fullWidth
            type="file"
            accept="image/png,image/jpeg,image/jpg"
            margin="normal"
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) {
                // Validate file type
                const validTypes = ['image/png', 'image/jpeg', 'image/jpg'];
                if (!validTypes.includes(file.type)) {
                  alert('Please upload only PNG or JPG image files.');
                  e.target.value = ''; // Clear the input
                  return;
                }
                setImageFile(file);
                const reader = new FileReader();
                reader.onloadend = () => {
                  setImagePreview(reader.result);
                };
                reader.readAsDataURL(file);
              }
            }}
            helperText="Upload a PNG or JPG image for the event"
            InputLabelProps={{ shrink: true }}
          />
          {imagePreview && (
            <Box sx={{ mt: 2, mb: 2 }}>
              <img
                src={imagePreview}
                alt="Preview"
                style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 4 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Events;
